<div
  snAssistantSurface="phoneNumber"
  i18n-surfaceTitle="@@phoneNumber.surface"
  surfaceTitle="Phone number">

<!-- Top Panel -->
@if (phoneNumberAgent) {
  <div class="phone-number-top-panel">
    <div class="top-panel-container">
      <!-- Left Section -->
      <div class="panel-left-section">
        <!-- Breadcrumbs -->
        <p-breadcrumb
          [model]="breadcrumbItems"
          styleClass="custom-breadcrumb compact-breadcrumb"
          [style]="{'--p-breadcrumb-padding': '0', '--p-breadcrumb-item-hover-color': 'var(--p-primary-color)'}">
        </p-breadcrumb>
      </div>

      <!-- Right Section -->
      <div class="panel-right-section">
        <p-button
          #menuButton
          icon="pi pi-ellipsis-h"
          severity="secondary"
          [outlined]="true"
          size="small"
          (click)="phoneMenu.toggle($event)">
        </p-button>

        <p-menu
          #phoneMenu
          [model]="menuItems"
          [popup]="true"
          [appendTo]="'body'"
          styleClass="custom-phone-menu">
        </p-menu>
      </div>
    </div>
  </div>
}

<div class="phone-number-edit-container">
  @if (isLoading) {
    <div class="loading-container">
      <p-progressSpinner />
      <p i18n="@@phoneNumberEdit.loading">Loading phone number...</p>
    </div>
  } @else if (phoneNumberAgent) {
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <div class="header-left">
          <h1>{{ phoneNumberAgent.phone_number || phoneNumberFallback }}</h1>
          <div class="metadata">
            <div class="metadata-line label-line">
              @if (!isEditingLabel) {
                <span>{{ provider || 'Twilio' }} - {{ phoneNumberAgent.label || '-' }}</span>
                <p-button
                  icon="pi pi-pencil"
                  size="small"
                  [text]="true"
                  [rounded]="true"
                  severity="secondary"
                  (click)="startEditingLabel()"
                  i18n-pTooltip="@@phoneNumberEdit.editLabel"
                  pTooltip="Edit label"
                  tooltipPosition="top">
                </p-button>
              } @else {
                <span>{{ provider || 'Twilio' }} - </span>
                <input
                  pInputText
                  name="label"
            [(ngModel)]="editedLabel"
                  (blur)="saveLabel()"
                  (keydown.enter)="saveLabel()"
                  (keydown.escape)="cancelEditLabel()"
                  class="label-input"
                  autoFocus />
              }
            </div>
            <div class="metadata-line id-line" (click)="copyId()" i18n-pTooltip="@@common.clickToCopy" pTooltip="Click to copy" tooltipPosition="top">
              {{ phoneNumberAgent.id }}
            </div>
          </div>
        </div>
        <div class="header-right">
          <p-button
            icon="pi pi-trash"
            severity="danger"
            [outlined]="true"
            (click)="deletePhoneNumber()"
            snAssistantAction="delete"
            actionDangerous
            actionHint="Disconnects the number from the workspace. Calls to it stop reaching any agent."
            i18n-pTooltip="@@phoneNumberEdit.deletePhone"
            pTooltip="Delete phone number"
            tooltipPosition="bottom"
            [disabled]="isSaving || isDeleting"
            i18n-ariaLabel="@@phoneNumberEdit.deletePhone"
            ariaLabel="Delete phone number">
          </p-button>
        </div>
      </div>
    </div>

    <p-divider />

    <!-- Form Container -->
    <div class="form-container">
      <!-- Agent Card -->
      <p-card>
        <div class="agent-section">
          <div class="agent-info">
            <div class="agent-header">
              <h3 i18n="@@nav.agents">Agents</h3>
              <p class="agent-description" i18n="@@phoneNumberEdit.agent.description">Assign an agent to handle calls to this phone number.</p>
            </div>
          </div>
          <div class="agent-select-container">
            @if (!agentsLoadError) {
              <p-select
                name="agent_id"
            [(ngModel)]="selectedAgentId"
                (ngModelChange)="onAgentChange()"
                [options]="agentOptions"
                i18n-placeholder="@@phoneNumberEdit.selectAgent"
                placeholder="Select an agent"
                [showClear]="true"
                [loading]="isLoadingAgents"
                [disabled]="isLoadingAgents || isSaving"
                [filter]="true"
                class="w-full">
              </p-select>
            } @else {
              <p-button
                i18n-label="@@phoneNumberEdit.reloadAgents"
                label="Reload Agents"
                icon="pi pi-refresh"
                severity="secondary"
                [outlined]="true"
                (click)="reloadAgents()">
              </p-button>
            }
          </div>
        </div>
      </p-card>

      <!-- SIP headers → agent variables (SKN-438) -->
      <p-card>
        <div class="agent-section">
          <div class="agent-info">
            <div class="agent-header">
              <h3 i18n="@@phoneNumberEdit.sipHeaders.title">SIP headers → agent variables</h3>
              <p class="agent-description" i18n="@@phoneNumberEdit.sipHeaders.description">
                Incoming calls: SIP headers with this prefix fill the agent's variables. With prefix X-, header X-minimumAmount fills variable minimumAmount. Only variables declared in the agent are filled. Leave empty to disable.
              </p>
            </div>
          </div>
          <div class="agent-select-container">
            <input
              pInputText
              name="sip_header_variable_prefix"
              [ngModel]="sipHeaderVariablePrefix()"
              (ngModelChange)="sipHeaderVariablePrefix.set($event)"
              i18n-placeholder="@@phoneNumberEdit.sipHeaders.placeholder"
              placeholder="e.g. X-"
              maxlength="64"
              [disabled]="isSaving"
              class="w-full" />
          </div>
        </div>
      </p-card>

      <!-- Test Call Card -->
      <p-card>
        <div class="test-call-section">
          <div class="test-call-header">
            <h3 i18n="@@phoneNumberEdit.testCall.title">Test call</h3>
            <p class="test-call-description" i18n="@@phoneNumberEdit.testCall.description">
              Initiate an outbound call from this phone number to a target number using the selected agent.
            </p>
          </div>

          @if (variableSchemaDict()) {
            <div class="test-call-variables">
              <label class="test-call-variables-label" i18n="@@agentTest.variables.title">Fill in variables</label>
              <!-- Динамические переменные номера: редактор схемы, правка одной
           строкой невозможна. -->
        <app-properties-edit
          snAssistantSkip
                [schemas]="[variableSchemaDict()!]"
                [values]="variableValues()"
                [required]="variableRequiredKeys()"
                (valuesChange)="onVariableValuesChange($event)"
                (validChange)="variablesValid.set($event)">
              </app-properties-edit>
            </div>
          }

          <div class="test-call-controls">
            <input
              pInputText
              snAssistantSkip
            [(ngModel)]="testCallTargetNumber"
              i18n-placeholder="@@phoneNumberEdit.testCall.targetPlaceholder"
              placeholder="Target phone number (E.164, e.g. +71234567890)"
              [disabled]="isTestingCall"
              class="test-call-input" />
            <p-button
              i18n-label="@@phoneNumberEdit.testCall.button"
              label="Call"
              icon="pi pi-phone"
              (click)="placeCall()"
              snAssistantAction="testCall"
              actionDangerous
              actionHint="Places a real outbound call from this number to the target above, so you can hear the agent live."
              [disabled]="!canPlaceCall"
              [loading]="isTestingCall"
              [pTooltip]="placeCallTooltip"
              tooltipPosition="top">
            </p-button>
          </div>

          @if (lastCall(); as call) {
            <div class="last-call-row">
              <div class="last-call-cell last-call-date">{{ call.created_at | date:'MMM d, yyyy, h:mm a' }}</div>
              <div class="last-call-cell last-call-to">{{ call.to_phone_number || '-' }}</div>
              <div class="last-call-cell">{{ formatDuration(call.duration_seconds) }}</div>
              <div class="last-call-cell">
                <p-tag
                  [value]="getCallStatusLabel(call.status)"
                  [severity]="getCallStatusSeverity(call.status)">
                </p-tag>
              </div>
              <div class="last-call-cell">
                @if (call.end_reason) {
                  <p-tag
                    [value]="getEndReasonLabel(call.end_reason)"
                    [severity]="getEndReasonSeverity(call.end_reason)">
                  </p-tag>
                } @else {
                  <span class="muted">—</span>
                }
              </div>
              <div class="last-call-cell last-call-actions">
                @if (call.agent_session_id && call.agent_id && !call.ended_at) {
                  <app-live-listen-button
                    [agentId]="call.agent_id"
                    [sessionId]="call.agent_session_id" />
                }
              </div>
            </div>
          }
        </div>
      </p-card>
    </div>
  }
</div>

<!-- Desktop Action Buttons -->
@if (!isLoading && phoneNumberAgent) {
  <div class="action-buttons">
    <p-button
      type="button"
      i18n-label="@@common.cancel"
      label="Cancel"
      severity="secondary"
      [outlined]="true"
      (click)="goBack()"
      [disabled]="isSaving">
    </p-button>
    <p-button
      type="button"
      [label]="saveBtnLabel"
      (click)="save()"
      [icon]="isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
      [disabled]="isSaving"
      [loading]="isSaving"
      snAssistantAction="save"
      actionHint="Writes the number settings — which agent answers on it. Field edits do nothing until this runs.">
    </p-button>
  </div>
}

<!-- Mobile Bottom Toolbar -->
@if (!isLoading && phoneNumberAgent) {
  <p-toolbar class="mobile-bottom-toolbar">
    <ng-template pTemplate="start">
      <p-button
        type="button"
        i18n-label="@@common.cancel"
        label="Cancel"
        severity="secondary"
        [outlined]="true"
        (click)="goBack()"
        size="small"
        [disabled]="isSaving">
      </p-button>
    </ng-template>

    <ng-template pTemplate="end">
      <p-button
        type="button"
        [label]="saveBtnLabel"
        (click)="save()"
        [icon]="isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
        [disabled]="isSaving"
        [loading]="isSaving"
        size="small">
      </p-button>
    </ng-template>
  </p-toolbar>
}

<!-- Delete Confirmation Dialog -->
<app-delete-confirmation
  [(visible)]="deleteDialogVisible"
  [title]="deleteTitle"
  [subtitle]="deleteSubtitle"
  [deleting]="isDeleting"
  (confirm)="onDeleteConfirm()"
  (cancel)="onDeleteCancel()" />

</div>
